import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./LandingLegalLotteries.module.css";
import LotteryMarquee from "./LotteryMarquee";
import { lotteryService } from "../../../../services/api";
import { DrawDatePicker } from "../../../../components/ui";
import ComingSoonBadge from "../../../../components/ui/ComingSoonBadge";
import {
  RESULTS_HISTORY_LIMIT,
  lotteriesFromResults,
  mapLotteryResultToGridItem,
} from "../../../../services/resultsMapper";
import { searchLotteryResults } from "../../../../services/resultsSearch";
import useResultDates from "../../../../hooks/useResultDates";

const PLACEHOLDER = `${process.env.PUBLIC_URL || ""}/images/lottery-placeholder.svg`;

function mapLotteryRow(row) {
  if (!row || typeof row !== "object") return null;
  const logo_url =
    row.logo_url ||
    row.image_url ||
    row.image ||
    row.logo ||
    row.img ||
    row.imagen ||
    null;
  const name = row.name || row.title || row.label || "Lotería";
  if (!logo_url && !name) return null;
  return {
    name,
    logo_url: logo_url || PLACEHOLDER,
    lottery_id: row.lottery_id || row.id || null,
    coming_soon: Boolean(row.coming_soon),
  };
}

function lotteryKeys(item) {
  return [item?.lottery_id, item?.name]
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v).trim().toLowerCase());
}

function handleLogoError(e) {
  if (e.currentTarget.src !== PLACEHOLDER) {
    e.currentTarget.src = PLACEHOLDER;
  }
}

/**
 * Logo de lotería: activa → Link a /apuestas; próximamente → tarjeta gris no interactiva.
 * `hidden`: copia decorativa del carrusel continuo (fuera del orden de tabulación).
 */
function LotteryLogoCard({ item, hidden = false }) {
  const img = (
    <img
      src={item.logo_url}
      alt={hidden ? "" : item.name}
      className={styles.logoImg}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={handleLogoError}
    />
  );

  if (item.coming_soon) {
    return (
      <div
        className={`${styles.logoCard} ${styles.logoCardSoon}`}
        aria-disabled="true"
        title={`${item.name}: próximamente`}
      >
        {img}
        <ComingSoonBadge size="sm" className={styles.soonBadge} />
      </div>
    );
  }

  return (
    <Link
      to="/apuestas"
      className={styles.logoCard}
      title={`Jugar ${item.name}`}
      tabIndex={hidden ? -1 : undefined}
      draggable={false}
    >
      {img}
    </Link>
  );
}

function uniqueLotteryItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item.lottery_id || item.name).trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * @param {object} props
 * @param {object} props.section
 * @param {object} [props.reveal]
 * @param {function} [props.sectionReveal]
 */
const LandingLegalLotteries = ({ section, reveal, sectionReveal }) => {
  const navigate = useNavigate();
  const [apiItems, setApiItems] = useState([]);
  const [comingSoonItems, setComingSoonItems] = useState([]);
  const [lotteries, setLotteries] = useState([]);
  const [rawResults, setRawResults] = useState([]);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [lotteryId, setLotteryId] = useState("");
  const [date, setDate] = useState("");
  const [searchError, setSearchError] = useState("");
  const [foundItems, setFoundItems] = useState(null);
  const [searching, setSearching] = useState(false);

  const cmsItems = useMemo(() => {
    const raw = section?.items;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map(mapLotteryRow).filter(Boolean);
  }, [section?.items]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      lotteryService.getLotteryInfo(),
      lotteryService.listResults({ limit: RESULTS_HISTORY_LIMIT }),
    ])
      .then(([infoRes, listRes]) => {
        if (cancelled) return;
        if (infoRes.ok) {
          const list = Array.isArray(infoRes.data) ? infoRes.data : [];
          setLotteries(list);
          if (cmsItems.length === 0) {
            setApiItems(list.map(mapLotteryRow).filter(Boolean));
          }
        }
        if (listRes.ok && Array.isArray(listRes.data)) {
          setRawResults(listRes.data);
        }
      })
      .catch(() => {
        /* network / CORS: sección se oculta si no hay items */
      });

    return () => {
      cancelled = true;
    };
  }, [cmsItems.length]);

  useEffect(() => {
    let cancelled = false;
    lotteryService
      .getComingSoonLotteries()
      .then((res) => {
        if (cancelled || !res.ok) return;
        setComingSoonItems(
          res.data
            .map(mapLotteryRow)
            .filter(Boolean)
            .map((item) => ({ ...item, coming_soon: true }))
        );
      })
      .catch(() => {
        /* Si falla, simplemente no se muestran las "Próximamente". */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const items = useMemo(() => {
    const soonKeys = new Set(comingSoonItems.flatMap(lotteryKeys));
    // Una lotería "Próximamente" nunca se muestra como jugable (aunque venga del CMS).
    const active = uniqueLotteryItems(cmsItems.length > 0 ? cmsItems : apiItems).filter(
      (item) => !lotteryKeys(item).some((k) => soonKeys.has(k))
    );
    return [...active, ...uniqueLotteryItems(comingSoonItems)];
  }, [cmsItems, apiItems, comingSoonItems]);
  const useCarousel = items.length > 3;
  const searchLotteries = useMemo(
    () => lotteriesFromResults(rawResults, lotteries),
    [rawResults, lotteries]
  );
  const { dates, loading: datesLoading } = useResultDates(
    lotteryId,
    searchLotteries,
    rawResults
  );
  const title =
    section?.title?.trim() || "Juega legal en las Loterías de Colombia";

  useEffect(() => {
    if (datesLoading) return;
    if (date && dates.length > 0 && !dates.includes(date)) {
      setDate("");
    }
  }, [date, dates, datesLoading]);

  if (items.length === 0 && lotteries.length === 0) {
    return null;
  }

  const sectionClass =
    sectionReveal && reveal
      ? `${styles.section} ${sectionReveal(reveal)}`
      : styles.section;

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!date) {
      setFoundItems([]);
      setSearchError("Selecciona una fecha de sorteo.");
      return;
    }
    setSearching(true);
    const result = await searchLotteryResults({ lotteryId, date }, searchLotteries);
    setSearching(false);
    if (!result.ok) {
      setFoundItems([]);
      setSearchError(result.error);
      return;
    }
    setSearchError("");
    setFoundItems(result.matches.map((r, i) => mapLotteryResultToGridItem(r, i)));
  };

  const handlePlay = (item) => {
    const lottery = lotteries.find(
      (l) => l.name === item.lotteryName || String(l.id) === String(item.raw?.lottery_id)
    );
    navigate("/apuestas", {
      state: lottery ? { selectedLottery: lottery } : undefined,
    });
  };

  return (
    <section
      ref={reveal?.ref}
      className={sectionClass}
      id="loterias-legales"
      aria-labelledby="legal-lotteries-title"
    >
      <div className={styles.inner}>
        {items.length > 0 && (
          <>
            <h2 id="legal-lotteries-title" className={styles.title}>
              {title}
            </h2>
            {section?.subtitle?.trim() && (
              <p className={styles.subtitle}>{section.subtitle}</p>
            )}
            <div
              className={`${styles.carouselWrap} ${useCarousel ? "" : styles.carouselStatic}`}
              aria-label="Logos de loterías oficiales de Colombia"
            >
              {useCarousel ? (
                <LotteryMarquee
                  items={items}
                  reduceMotion={reduceMotion}
                  getKey={(item, index) =>
                    `${item.coming_soon ? "soon" : "on"}-${item.lottery_id || item.name}-${index}`
                  }
                  renderItem={(item, { hidden }) => (
                    <LotteryLogoCard item={item} hidden={hidden} />
                  )}
                />
              ) : (
                <div className={styles.staticRow}>
                  {items.map((item, index) => (
                    <LotteryLogoCard
                      key={`${item.coming_soon ? "soon" : "on"}-${item.lottery_id || item.name}-${index}`}
                      item={item}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.searchBlock} id="consulta-resultados">
          <h3 className={styles.searchTitle}>
            Consulta los resultados de las loterías aquí
          </h3>
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <label className={styles.field} htmlFor="landing-lottery">
              <span className={styles.fieldLabel}>Lotería</span>
              <select
                id="landing-lottery"
                className={styles.searchSelect}
                value={lotteryId}
                onChange={(e) => setLotteryId(e.target.value)}
              >
                <option value="">Todas</option>
                {searchLotteries.map((lottery) => (
                  <option key={lottery.id} value={lottery.id}>
                    {lottery.name}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.field}>
              <span className={styles.fieldLabel} id="landing-date-label">
                Fecha del sorteo
              </span>
              <DrawDatePicker
                id="landing-date"
                dates={dates}
                value={date}
                onChange={setDate}
                placeholder="Seleccionar fecha"
              />
            </div>
            <button
              type="submit"
              className={styles.searchBtn}
              disabled={searching}
              aria-busy={searching}
            >
              {searching ? "Buscando…" : "Buscar"}
            </button>
          </form>

          {searchError && (
            <p className={styles.searchError} role="status">
              {searchError}
            </p>
          )}

          {foundItems?.length > 0 && (
            <div className={styles.resultsGrid} aria-live="polite">
              {foundItems.map((item) => (
                <article key={item.id} className={styles.resultCard}>
                  <div className={styles.resultHead}>
                    <h4 className={styles.resultName}>{item.lotteryName}</h4>
                    <p className={styles.resultDate}>{item.date}</p>
                  </div>
                  <div className={styles.resultNumbers}>
                    <div>
                      <div className={styles.digits}>
                        {item.numberDigits.map((digit, index) => (
                          <span key={`n-${index}`} className={styles.digit}>
                            {digit}
                          </span>
                        ))}
                      </div>
                      <span className={styles.digitLabel}>Número</span>
                    </div>
                    {item.seriesDigits.length > 0 && (
                      <div>
                        <div className={styles.digits}>
                          {item.seriesDigits.map((digit, index) => (
                            <span key={`s-${index}`} className={styles.digit}>
                              {digit}
                            </span>
                          ))}
                        </div>
                        <span className={styles.digitLabel}>Serie</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.playBtn}
                    onClick={() => handlePlay(item)}
                  >
                    Jugar
                  </button>
                </article>
              ))}
            </div>
          )}

          <Link
            className={styles.moreLink}
            to={
              lotteryId || date
                ? `/resultados?lottery=${encodeURIComponent(lotteryId)}&date=${encodeURIComponent(date)}`
                : "/resultados"
            }
          >
            Ver historial de resultados
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LandingLegalLotteries;
